import axios, {
    AxiosAdapter,
    AxiosRequestConfig,
    InternalAxiosRequestConfig,
} from 'axios'
import { handleApiError, markHandledUnauthorizedError } from './errorHandler'
import { resolveApiBaseUrl } from './apiBaseUrl'
import { extractApiErrorDetailPayload } from './apiErrorI18n'

export const AUTH_REDIRECT_REASON_STORAGE_KEY = 'auth_redirect_reason'
export const POST_LOGIN_REDIRECT_STORAGE_KEY = 'post_login_redirect'
export const AUTH_REDIRECT_REASON_SESSION_EXPIRED = 'session-expired'
export const AUTH_REDIRECT_REASON_PERMISSION_CHANGED = 'permission-changed'
export const AUTH_REDIRECT_REASON_PASSWORD_CHANGED = 'password-changed'
export const AUTH_REDIRECT_REASON_ACCOUNT_CHANGED = 'account-changed'

const SESSION_INVALIDATED_CODE = 'SESSION_INVALIDATED'
const AUTH_REDIRECT_REASONS = new Set([
    AUTH_REDIRECT_REASON_PERMISSION_CHANGED,
    AUTH_REDIRECT_REASON_PASSWORD_CHANGED,
    AUTH_REDIRECT_REASON_ACCOUNT_CHANGED,
])

let isHandlingUnauthorized = false
type AuthRedirectHandler = (target: string) => void
const defaultAuthRedirectHandler: AuthRedirectHandler = (target) =>
    window.location.replace(target)
let authRedirectHandler = defaultAuthRedirectHandler

export const setAuthRedirectHandler = (
    handler?: AuthRedirectHandler
): void => {
    authRedirectHandler = handler ?? defaultAuthRedirectHandler
    isHandlingUnauthorized = false
}

const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete'])
const IDEMPOTENCY_HEADER = 'X-Idempotency-Key'
const inFlightMutationRequests = new Map<string, ReturnType<AxiosAdapter>>()
const DEFAULT_ERROR_DEDUPE_WINDOW_MS = 1500

const AUTHORIZATION_HEADER_NAMES = ['Authorization', 'authorization'] as const

const getAuthorizationHeader = (headers: unknown): unknown => {
    if (!headers || typeof headers !== 'object') {
        return null
    }

    if (typeof (headers as { get?: unknown }).get === 'function') {
        const axiosHeaderValue = (
            headers as { get: (name: string) => unknown }
        ).get('Authorization')
        if (axiosHeaderValue) {
            return axiosHeaderValue
        }
    }

    for (const headerName of AUTHORIZATION_HEADER_NAMES) {
        if (headerName in (headers as Record<string, unknown>)) {
            return (headers as Record<string, unknown>)[headerName]
        }
    }

    return null
}

export const extractBearerToken = (
    authorizationHeader: unknown
): string | null => {
    const headerValue = Array.isArray(authorizationHeader)
        ? authorizationHeader[0]
        : authorizationHeader

    if (typeof headerValue !== 'string') {
        return null
    }

    const trimmedHeaderValue = headerValue.trim()
    if (!trimmedHeaderValue.toLowerCase().startsWith('bearer ')) {
        return null
    }

    const token = trimmedHeaderValue.slice(7).trim()
    return token || null
}

export const shouldHandleUnauthorizedForCurrentSession = (
    authorizationHeader: unknown,
    currentToken: string | null
): boolean => {
    if (!currentToken) {
        return false
    }

    const requestToken = extractBearerToken(authorizationHeader)

    if (!requestToken) {
        return true
    }

    return requestToken === currentToken
}

export const getAuthRedirectReasonFromUnauthorizedError = (
    error: unknown
): string => {
    const detail = extractApiErrorDetailPayload(error)
    if (!detail || typeof detail !== 'object') {
        return AUTH_REDIRECT_REASON_SESSION_EXPIRED
    }

    const code = (detail as { code?: unknown }).code
    const reason = (detail as { reason?: unknown }).reason
    if (
        code === SESSION_INVALIDATED_CODE &&
        typeof reason === 'string' &&
        AUTH_REDIRECT_REASONS.has(reason)
    ) {
        return reason
    }

    return AUTH_REDIRECT_REASON_SESSION_EXPIRED
}

const isSessionUnauthorizedError = (error: unknown): boolean => {
    const detail = extractApiErrorDetailPayload(error)

    if (!detail || typeof detail !== 'object') {
        return true
    }

    return (detail as { code?: unknown }).code === SESSION_INVALIDATED_CODE
}

export interface MutationRequestOptions {
    dedupeKey?: string
    idempotencyKey?: string
    preventDuplicate?: boolean
}

declare module 'axios' {
    export interface AxiosRequestConfig {
        dedupeKey?: string
        preventDuplicate?: boolean
    }
}

export const getMutationRequestConfig = (
    options: MutationRequestOptions = {}
): AxiosRequestConfig => {
    const config: AxiosRequestConfig = {}

    if (options.idempotencyKey) {
        config.headers = {
            [IDEMPOTENCY_HEADER]: options.idempotencyKey,
        }
    }
    if (options.preventDuplicate !== undefined) {
        config.preventDuplicate = options.preventDuplicate
    } else {
        config.preventDuplicate = true
    }
    if (options.dedupeKey) {
        config.dedupeKey = options.dedupeKey
    }

    return config
}

const normalizeRequestData = (data: unknown): string => {
    if (data === undefined || data === null) {
        return ''
    }
    if (typeof data === 'string') {
        return data
    }
    try {
        return JSON.stringify(data)
    } catch {
        return String(data)
    }
}

const buildRequestDedupeKey = (config: InternalAxiosRequestConfig): string => {
    if (config.dedupeKey) {
        return config.dedupeKey
    }

    const method = (config.method || 'get').toUpperCase()
    return [
        method,
        config.baseURL || '',
        config.url || '',
        normalizeRequestData(config.params),
        normalizeRequestData(config.data),
    ].join('|')
}

const shouldPreventDuplicateMutation = (
    config: InternalAxiosRequestConfig
): boolean => {
    const method = (config.method || 'get').toLowerCase()
    return MUTATION_METHODS.has(method) && config.preventDuplicate !== false
}

// 检查并确保baseURL配置正确
const api = axios.create({
    baseURL: resolveApiBaseUrl(),
    timeout: 15000,
    headers: {
        'Content-Type': 'application/json',
    },
})

export const createDuplicatePreventingAdapter = (
    adapter: AxiosAdapter
): AxiosAdapter => (config) => {
    if (!shouldPreventDuplicateMutation(config)) {
        return adapter(config)
    }

    const requestKey = buildRequestDedupeKey(config)
    const inFlightRequest = inFlightMutationRequests.get(requestKey)
    if (inFlightRequest) {
        return inFlightRequest
    }

    const request = adapter(config).finally(() => {
        inFlightMutationRequests.delete(requestKey)
    })
    inFlightMutationRequests.set(requestKey, request)
    return request
}

const getRateLimitedErrorKey = (error: unknown): string | null => {
    if (!axios.isAxiosError(error) || error.response?.status !== 403) {
        return null
    }

    const detail = (error.response.data as { detail?: unknown } | undefined)
        ?.detail
    return `403:${String(detail ?? error.config?.url ?? '')}`
}

export const createRateLimitedErrorHandler = (
    options: {
        windowMs?: number
        now?: () => number
    } = {}
) => {
    const shownAtByKey = new Map<string, number>()
    const windowMs = options.windowMs ?? DEFAULT_ERROR_DEDUPE_WINDOW_MS
    const now = options.now ?? Date.now

    return (error: unknown) => {
        const key = getRateLimitedErrorKey(error)
        if (key) {
            const currentTime = now()
            const lastShownAt = shownAtByKey.get(key)
            if (
                lastShownAt !== undefined &&
                currentTime - lastShownAt < windowMs
            ) {
                return
            }
            shownAtByKey.set(key, currentTime)
        }

        handleApiError(error)
    }
}

const defaultAdapter = axios.getAdapter(api.defaults.adapter || 'xhr')
api.defaults.adapter = createDuplicatePreventingAdapter(defaultAdapter)
const handleRateLimitedApiError = createRateLimitedErrorHandler()

// 添加请求拦截器，自动附加token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => Promise.reject(error)
)

// 响应拦截器
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const currentToken = localStorage.getItem('accessToken')
            const shouldHandleUnauthorized =
                shouldHandleUnauthorizedForCurrentSession(
                    getAuthorizationHeader(error.config?.headers),
                    currentToken
                ) && isSessionUnauthorizedError(error)

            if (!shouldHandleUnauthorized) {
                handleRateLimitedApiError(error)
                return Promise.reject(error)
            }

            markHandledUnauthorizedError(error)
            localStorage.removeItem('accessToken')
            delete api.defaults.headers.common.Authorization

            const currentLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`
            if (window.location.pathname !== '/login') {
                sessionStorage.setItem(
                    AUTH_REDIRECT_REASON_STORAGE_KEY,
                    getAuthRedirectReasonFromUnauthorizedError(error)
                )
                sessionStorage.setItem(
                    POST_LOGIN_REDIRECT_STORAGE_KEY,
                    currentLocation
                )
            }

            if (!isHandlingUnauthorized) {
                isHandlingUnauthorized = true

                if (window.location.pathname !== '/login') {
                    authRedirectHandler('/login')
                }
            }

            return Promise.reject(error)
        }

        // 使用错误处理工具
        handleRateLimitedApiError(error)
        return Promise.reject(error)
    }
)

export default api
